
import React, { useEffect, useRef } from "react";

interface YouTubeEmbedProps {
  videoId: string;
  autoplay?: boolean;
  controls?: boolean;
  mute?: boolean;
}

const YouTubeEmbedSection: React.FC<YouTubeEmbedProps> = ({ 
  videoId, 
  autoplay = false, 
  controls = true,
  mute = false 
}) => {
  const playerRef = useRef<HTMLDivElement>(null);
  const playerInstanceRef = useRef<any>(null);
  const qualityIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Defer YouTube API loading using requestIdleCallback to reduce main thread blocking
    const loadYouTubeAPI = () => {
      if (!(window as any).YT) {
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        tag.async = true;
        
        const firstScriptTag = document.getElementsByTagName("script")[0];
        firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
        
        (window as any).onYouTubeIframeAPIReady = initPlayer;
      } else {
        initPlayer();
      }
    };

    // Use requestIdleCallback to defer loading when browser is idle
    if ('requestIdleCallback' in window) {
      const idleCallback = requestIdleCallback(loadYouTubeAPI, { timeout: 6000 });
      
      return () => {
        cancelIdleCallback(idleCallback);
        // Clean up player instance and quality interval
        if (playerInstanceRef.current) {
          playerInstanceRef.current.destroy();
        }
        if (qualityIntervalRef.current) {
          clearInterval(qualityIntervalRef.current);
        }
      };
    } else {
      // Fallback for browsers that don't support requestIdleCallback
      const timeout = setTimeout(loadYouTubeAPI, 1000);
      
      return () => {
        clearTimeout(timeout);
        // Clean up player instance and quality interval
        if (playerInstanceRef.current) {
          playerInstanceRef.current.destroy();
        }
        if (qualityIntervalRef.current) {
          clearInterval(qualityIntervalRef.current);
        }
      };
    }
  }, [videoId]);

  const forceHDQuality = (player: any) => {
    try {
      const availableQualities = player.getAvailableQualityLevels();
      console.log("Qualités disponibles:", availableQualities);
      
      if (availableQualities.includes('hd1080')) {
        player.setPlaybackQuality('hd1080');
        console.log("Qualité forcée à HD1080");
      } else if (availableQualities.includes('hd720')) {
        player.setPlaybackQuality('hd720');
        console.log("Qualité forcée à HD720");
      } else if (availableQualities.includes('large')) {
        player.setPlaybackQuality('large');
        console.log("Qualité forcée à Large (480p)");
      }
    } catch (error) {
      console.error("Erreur lors du forçage de la qualité HD:", error);
    }
  };

  const initPlayer = () => {
    if (!playerRef.current) return;
    
    console.info("Section héros montée avec vidéo YouTube");
    
    try {
      playerInstanceRef.current = new (window as any).YT.Player(playerRef.current, {
        videoId,
        width: '100%',
        height: '100%',
        playerVars: {
          autoplay: autoplay ? 1 : 0,
          mute: mute ? 1 : 0,
          controls: controls ? 1 : 0,
          disablekb: controls ? 0 : 1,
          fs: controls ? 1 : 0,
          iv_load_policy: 3,
          modestbranding: 1,
          playsinline: 1,
          rel: 0,
          showinfo: 0,
          vq: 'hd720',
          hd: 1,
          loading: 'lazy',
        },
        events: {
          onReady: (event: any) => {
            console.log("Player ready - forcing HD quality");
            
            // Force HD quality immediately
            setTimeout(() => forceHDQuality(event.target), 500);
            setTimeout(() => forceHDQuality(event.target), 2000);
            
            // Set up continuous quality monitoring
            qualityIntervalRef.current = setInterval(() => {
              forceHDQuality(event.target);
            }, 3000);
          },
          onStateChange: (event: any) => {
            console.log("State changed:", event.data);
            
            // Force HD quality on every state change
            setTimeout(() => forceHDQuality(event.target), 100);
            
            
            // Force HD when playing starts
            if (event.data === (window as any).YT.PlayerState.PLAYING) {
              console.log("Video playing - ensuring HD quality");
              setTimeout(() => forceHDQuality(event.target), 100);
            }
          },
        },
      });
    } catch (error) {
      console.error("Erreur d'initialisation du lecteur YouTube:", error);
    }
  };

  return (
    <div className="relative w-full h-full flex justify-center items-center">
      <div ref={playerRef} className="absolute inset-0 w-full h-full" />
      {!controls && <div className="absolute inset-0 w-full h-full pointer-events-auto cursor-default" />}
    </div>
  );
};

export default YouTubeEmbedSection;
